import React from 'react';
import {ScrollView, View} from 'react-native';
import {MaterialCommunityIcons} from '@expo/vector-icons';
import CCText from '@/Atoms/CCText';
import {useInterClubBoard} from '@/Hooks/ClubsApi/useClubsQueries';
import {InterClubRow} from '@/Hooks/ClubsApi/types';
import InterClubBoardSkeleton from './InterClubBoardSkeleton';
import ClubBadge from './ClubBadge';
import {BADGES, DEFAULT_BADGE_BG} from '../badgeOptions';
import type {PieceKey} from '../clubPieces';

const pieceFor = (badgeId?: string): PieceKey =>
  (BADGES.find(badge => badge.id === badgeId)?.piece ?? 'pawn') as PieceKey;

const TrendBadge = ({ trend, delta }: { trend: string; delta: number }) => {
  if (trend === "new" || delta === 0) return null;
  const up = trend === "up";
  return (
    <CCText fontSize={12} color={up ? "#1E9E63" : "#C8433B"} fontWeight="600">
      {up ? "▲" : "▼"}{Math.abs(delta)}
    </CCText>
  );
};

export default function InterClubBoard({
  scope,
  limit = 3,
}: {
  scope: 'public' | 'private';
  limit?: number;
}) {
  const { data, isLoading } = useInterClubBoard(scope, 'all', limit);

  const renderRow = (item: InterClubRow) => {
    return (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 11,
          paddingVertical: 8,
          paddingHorizontal: 11,
          borderRadius: 12,
          backgroundColor: '#fff',
          shadowColor: '#4D3F37',
          shadowOffset: {width: 0, height: 1},
          shadowOpacity: 0.05,
          shadowRadius: 2,
          elevation: 1,
        }}>
        <View
          testID={`inter-club-badge-${item.club_id}`}
          accessibilityLabel={`${item.name} club badge`}>
          <ClubBadge
            piece={pieceFor(item.badge_id)}
            color={item.background_color || DEFAULT_BADGE_BG}
            size={32}
            radius={9}
          />
        </View>
        <View style={{flex: 1, minWidth: 0}}>
          <View
            testID={`inter-club-name-row-${item.club_id}`}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 4,
            }}>
            <CCText
              numberOfLines={1}
              style={{
                flexShrink: 1,
                fontSize: 14.5,
                fontWeight: '600',
                color: '#262322',
              }}>
              {item.name}
            </CCText>
            {item.official ? (
              <View
                testID={`inter-club-official-${item.club_id}`}
                accessibilityLabel="Official club"
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 9,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <MaterialCommunityIcons
                  name="check-decagram"
                  size={18}
                  color="#D2A51F"
                />
              </View>
            ) : null}
            {item.visibility === 'private' ? (
              <View
                testID={`inter-club-private-${item.club_id}`}
                accessibilityLabel="Private club"
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 9,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#F3EEFF',
                }}>
                <MaterialCommunityIcons
                  name="lock"
                  size={11}
                  color="#6948C4"
                />
              </View>
            ) : null}
          </View>
          <CCText style={{fontSize: 11.5, color: '#888280', marginTop: 1}}>
            {item.players} players
          </CCText>
        </View>
        <View style={{alignItems: 'center', gap: 4}}>
          <CCText
            style={{
              fontSize: 14,
              fontWeight: '700',
              color: '#4D3F37',
            }}>
            {item.points}
            <CCText style={{fontSize: 11, fontWeight: '500', color: '#888280'}}>
              {' '}
              pts
            </CCText>
          </CCText>
          <TrendBadge trend={item.trend} delta={item.delta} />
        </View>
      </View>
    );
  };

  if (isLoading) {
    return <InterClubBoardSkeleton count={limit} />;
  }

  return (
    <View style={{flex: 1}}>
      <View
        style={{
          paddingHorizontal: 14,
          paddingTop: 12,
          paddingBottom: 6,
        }}>
        <CCText style={{fontSize: 13, fontWeight: '700', color: '#262322'}}>
          Top Performing Clubs
        </CCText>
      </View>
      <ScrollView
        contentContainerStyle={{paddingHorizontal: 14, paddingTop: 4, gap: 6}}
      >
        {(data?.rows ?? []).map(item => (
          <View key={String(item.club_id)}>{renderRow(item)}</View>
        ))}
      </ScrollView>
    </View>
  );
}
